import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
    FinaPagoMobilePaymentData,
    FinaPayment,
    FinaPaymentsActivationRequest,
    FinaPaymentsActivationResponse,
    FinaPaymentsChargeRequest,
    FinaPaymentsSettings,
    FinaPaymentsSettingsResponse,
    FinaPaymentsStaticQr,
} from '../models/fina-payments.model';
import { FinaPagoAccessService } from './fina-pago-access.service';

// Fina Pago (cobros con QR). Patrón de `bank-accounts.service.ts`: HttpClient + `environment.API_URL`
// y un BehaviorSubject con la configuración del comercio que Cuentas bancarias y el POS comparten.
@Injectable({
    providedIn: 'root',
})
export class FinaPaymentsService {
    API_URL = environment.API_URL;

    private _settings$ = new BehaviorSubject<FinaPaymentsSettings | null>(null);
    /** Cuentas del comercio en Fina Pago (la predeterminada primero). */
    private _accounts$ = new BehaviorSubject<FinaPaymentsSettings[]>([]);
    private _multipleAccountsSupported = false;
    private _canSetDefaultAccount = false;
    private _settingsLoaded$ = new BehaviorSubject<boolean>(false);
    private _termsUrl = '';
    private _defaultFeePercent = 1;
    private _mobilePaymentData: FinaPagoMobilePaymentData | null = null;
    /** Hubo una consulta de settings mientras el flag estaba apagado: al encenderse se vuelve a pedir. */
    private _skippedWhileDisabled = false;

    constructor(private http: HttpClient, private access: FinaPagoAccessService) {
        // PostHog resuelve los flags ~1,5 s después del init: si alguien pidió settings antes, se recargan solos.
        this.access.canAccessChanges$().subscribe(enabled => {
            if (enabled && this._skippedWhileDisabled) {
                this._skippedWhileDisabled = false;
                this.refreshSettings();
            }
        });
    }

    /** Flag `finapago`: con `false` el módulo entero queda oculto (promo, banner, activación, POS y CxC). */
    get enabled(): boolean {
        return this.access.canAccessSync();
    }

    /** Valor actual del flag y cada cambio posterior. */
    get enabled$(): Observable<boolean> {
        return this.access.canAccessChanges$();
    }

    /** Comisión de Fina con la que arranca una activación nueva. */
    get defaultFeePercent(): number {
        return this._defaultFeePercent;
    }

    /** Comisión vigente del comercio (si está activo) o la inicial. */
    get currentFeePercent(): number {
        return this._settings$.value?.feePercent ?? this._defaultFeePercent;
    }

    get settings$(): BehaviorSubject<FinaPaymentsSettings | null> {
        return this._settings$;
    }

    get accounts$(): BehaviorSubject<FinaPaymentsSettings[]> {
        return this._accounts$;
    }

    /** ¿Se puede asociar más de una cuenta? (depende del proveedor). */
    get multipleAccountsSupported(): boolean {
        return this._multipleAccountsSupported;
    }

    /** ¿Se puede cambiar la cuenta predeterminada? (depende del proveedor y sus credenciales). */
    get canSetDefaultAccount(): boolean {
        return this._canSetDefaultAccount;
    }

    get settingsLoaded$(): BehaviorSubject<boolean> {
        return this._settingsLoaded$;
    }

    get termsUrl(): string {
        return this._termsUrl;
    }

    /** Datos de pago móvil que el comercio da a sus clientes tras activar (`null` si el ambiente no los define). */
    get mobilePaymentData(): FinaPagoMobilePaymentData | null {
        return this._mobilePaymentData;
    }

    /** Fina Pago operativo para cobrar: flag encendido y cuenta ACTIVE. */
    get isActive(): boolean {
        return this.enabled && this._settings$.value?.status === 'ACTIVE';
    }

    getSettings$(): Observable<FinaPaymentsSettingsResponse> {
        // Flag apagado: ni se consulta al api. Se deja el estado vacío y «cargado» para que nadie espere.
        if (!this.enabled) {
            this._skippedWhileDisabled = true;
            this._settings$.next(null);
            this._accounts$.next([]);
            this._settingsLoaded$.next(true);
            return of({ settings: null, history: [], accounts: [], termsUrl: '', provider: 'live', defaultFeePercent: this._defaultFeePercent } as FinaPaymentsSettingsResponse);
        }
        return this.http.get<FinaPaymentsSettingsResponse>(`${this.API_URL}/payments/settings`).pipe(
            tap(res => {
                this._settings$.next(res?.settings ?? null);
                this._accounts$.next(res?.accounts || []);
                this._multipleAccountsSupported = res?.multipleAccountsSupported === true;
                this._canSetDefaultAccount = res?.canSetDefaultAccount === true;
                this._termsUrl = res?.termsUrl || '';
                if (Number.isFinite(Number(res?.defaultFeePercent))) this._defaultFeePercent = Number(res.defaultFeePercent);
                this._mobilePaymentData = res?.mobilePaymentData ?? null;
                this._settingsLoaded$.next(true);
            }),
        );
    }

    // Re-consulta sin que el caller tenga que suscribirse (el modal de activación lo usa al cerrar).
    refreshSettings(): void {
        this.getSettings$().subscribe({ error: () => this._settingsLoaded$.next(true) });
    }

    activate$(body: FinaPaymentsActivationRequest): Observable<FinaPaymentsActivationResponse> {
        return this.http.post<FinaPaymentsActivationResponse>(`${this.API_URL}/payments/activation`, body).pipe(
            tap(res => {
                // Una cuenta adicional (OTP pendiente) no reemplaza a la predeterminada del comercio.
                if (res?.settings && (res.settings.isDefault || res.settings.status !== 'VERIFYING' || !this._settings$.value)) this._settings$.next(res.settings);
            }),
        );
    }

    /** Cuenta adicional, paso 2: código de 8 dígitos que el banco envió por SMS. */
    confirmAccount$(settingsId: string, otp: string, makeDefault = false): Observable<FinaPaymentsActivationResponse> {
        return this.http.post<FinaPaymentsActivationResponse>(`${this.API_URL}/payments/accounts/${settingsId}/confirm`, { otp, makeDefault });
    }

    /**
     * Comercio integrado, verificación bancaria inicial: el código de 8 dígitos (persona natural) o los dos
     * microabonos como string de 3 dígitos (persona jurídica, `0,36` → `"036"`). Con éxito la cuenta queda activa.
     */
    verifyCommerce$(settingsId: string, body: { otp?: string; amount1?: string; amount2?: string }): Observable<FinaPaymentsActivationResponse> {
        return this.http.post<FinaPaymentsActivationResponse>(`${this.API_URL}/payments/accounts/${settingsId}/verify`, body).pipe(
            tap(res => {
                if (res?.settings) this._settings$.next(res.settings);
            }),
        );
    }

    /** Comercio integrado: pide otro código OTP para la verificación inicial (misma alta). */
    /** Abandona la verificación pendiente para corregir los datos y volver a empezar. */
    cancelVerification$(settingsId: string): Observable<FinaPaymentsActivationResponse> {
        return this.http.post<FinaPaymentsActivationResponse>(`${this.API_URL}/payments/accounts/${settingsId}/cancel-verification`, {});
    }

    resendVerification$(settingsId: string): Observable<FinaPaymentsActivationResponse> {
        return this.http.post<FinaPaymentsActivationResponse>(`${this.API_URL}/payments/accounts/${settingsId}/resend`, {});
    }

    /** Cambia la cuenta que recibe los cobros. */
    setDefaultAccount$(settingsId: string): Observable<FinaPaymentsActivationResponse> {
        return this.http.post<FinaPaymentsActivationResponse>(`${this.API_URL}/payments/accounts/${settingsId}/default`, {});
    }

    createCharge$(body: FinaPaymentsChargeRequest): Observable<{ payment: FinaPayment }> {
        return this.http.post<{ payment: FinaPayment }>(`${this.API_URL}/payments/charges`, body);
    }

    // `refresh=1` fuerza la consulta al proveedor («Verificar ahora» y el polling del modal).
    getCharge$(paymentId: string, refresh = false): Observable<{ payment: FinaPayment }> {
        const params = refresh ? new HttpParams().set('refresh', '1') : undefined;
        return this.http.get<{ payment: FinaPayment }>(`${this.API_URL}/payments/charges/${paymentId}`, { params });
    }

    cancelCharge$(paymentId: string): Observable<{ payment: FinaPayment }> {
        return this.http.post<{ payment: FinaPayment }>(`${this.API_URL}/payments/charges/${paymentId}/cancel`, {});
    }

    // ---- QR fijo del comercio (`/pay/m/<code>`) ----

    /** QR activo del comercio o `null` si aún no lo generó. */
    getStaticQr$(): Observable<{ qr: FinaPaymentsStaticQr | null }> {
        return this.http.get<{ qr: FinaPaymentsStaticQr | null }>(`${this.API_URL}/payments/qr`);
    }

    /** Genera un QR nuevo; el anterior (impreso) deja de funcionar. */
    regenerateStaticQr$(): Observable<{ qr: FinaPaymentsStaticQr }> {
        return this.http.post<{ qr: FinaPaymentsStaticQr }>(`${this.API_URL}/payments/qr`, {});
    }

    /** Bandeja «Cobros recibidos»: cobros QR pagados sin aplicar a una venta. */
    getUnassignedCharges$(limit = 50): Observable<{ payments: FinaPayment[] }> {
        const params = new HttpParams().set('unassigned', '1').set('limit', String(limit));
        return this.http.get<{ payments: FinaPayment[] }>(`${this.API_URL}/payments/charges`, { params });
    }

    /** La caja reserva el cobro QR para su venta antes de aplicarlo (409 si otra caja ganó). */
    claimCharge$(paymentId: string, saleId: string): Observable<{ payment: FinaPayment }> {
        return this.http.post<{ payment: FinaPayment }>(`${this.API_URL}/payments/charges/${paymentId}/claim`, { saleId });
    }
}
